import './SearchCombobox.scss';
import cn from 'classnames';
import { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Overlay } from 'react-bootstrap';
import { Menu, MenuRow, useMenuKeyboardNav } from '../Menu';
import { useDebouncedValue } from '../../utils/hooks/useDebouncedValue';
import { useLabelCache } from '../../utils/hooks/useLabelCache';
import { Size, SizeType } from '../designConstants';
import { DropdownOption } from '../Dropdown/dropdown-constants';
import { SearchField } from '../SearchField/SearchField';
import { DEBOUNCE_MS } from './search-combobox-constants';

interface SearchComboboxProps<T extends string> {
  value: T | null;
  onChange: (value: T | null) => void;
  optionFetcher: (input: string) => Promise<DropdownOption<T>[]>;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  className?: string;
  formatSelectedLabel?: (value: T) => string;
}

const optionLabel = <T extends string>(option: DropdownOption<T>): string =>
  typeof option === 'string' ? option : option.label || option.value;

const optionValue = <T extends string>(option: DropdownOption<T>): T =>
  typeof option === 'string' ? option : option.value;

export const SearchCombobox = <T extends string>({
  value,
  onChange,
  optionFetcher,
  placeholder,
  disabled = false,
  size = Size.MD,
  className,
  formatSelectedLabel
}: SearchComboboxProps<T>) => {
  // `query === null` means "show derived label"; any string (including '')
  // means the user is editing.
  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<DropdownOption<T>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const listboxId = useId();

  const inputRef = useRef<HTMLInputElement>(null);
  // Stored as state (not a ref) so <Overlay target={...}> doesn't read a ref
  // during render — the React 19 react-hooks/refs rule disallows that.
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const latestRequestId = useRef(0);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Remember labels for every result we've seen, so a selected value keeps its
  // friendly label even after the user types something that filters it out.
  const labelCache = useLabelCache<T, DropdownOption<T>>(results, optionValue, optionLabel);

  // Default label resolver: lookup against the most recent results, then the
  // accumulated cache, then fall back to the raw value.
  const resolveSelectedLabel = useCallback(
    (val: T): string => {
      if (formatSelectedLabel) return formatSelectedLabel(val);
      const found = results.find(r => optionValue(r) === val);
      if (found) return optionLabel(found);
      const cached = labelCache.get(val);
      return cached ?? val;
    },
    [formatSelectedLabel, results, labelCache]
  );

  const resolvedValueLabel = value !== null ? resolveSelectedLabel(value) : '';
  const displayValue = query !== null ? query : resolvedValueLabel;

  const handleFocus = () => {
    if (disabled) return;
    if (query === null) {
      setQuery(value !== null ? resolveSelectedLabel(value) : '');
    }
    // Open the popover only when there's a selection to show options for.
    // For an empty/no-selection input, the popover stays closed until the user types.
    if (value !== null) {
      setIsOpen(true);
    }
    // Position cursor at end after React commits the new value.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  };

  const handleBlur = () => {
    // Defer close so an in-flight row selection can still commit. The popover's
    // onMouseDown={preventDefault} keeps mouse clicks from blurring the input,
    // but keyboard flows (arrow-key + Enter → handleSelectByValue → inputRef
    // .blur()) race with any pending row click; the 150ms window is enough for
    // React to flush the resulting state updates before the popover unmounts.
    blurTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      setQuery(null);
      blurTimerRef.current = null;
    }, 150);
  };

  const handleSelectByValue = (val: T) => {
    onChange(val);
    setQuery(null);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const menuRows: MenuRow<T>[] = useMemo(
    () => results.map(r => ({ value: optionValue(r), content: optionLabel(r) })),
    [results]
  );

  const {
    highlightedIndex,
    setHighlightedIndex,
    onKeyDown: menuKeyDown
  } = useMenuKeyboardNav({
    rows: menuRows,
    onSelect: row => handleSelectByValue(row.value),
    onEscape: () => setIsOpen(false)
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Tab is SearchCombobox-specific (close on blur-out) — handle locally and let
    // the hook take everything else.
    if (e.key === 'Tab') {
      setIsOpen(false);
      return;
    }
    if (!isOpen) {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
      return;
    }
    menuKeyDown(e);
  };

  const debouncedQuery = useDebouncedValue(query ?? '', DEBOUNCE_MS);

  useEffect(() => {
    // Don't fetch when the input isn't being edited (query === null).
    if (query === null) return undefined;

    let cancelled = false;
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;

    setIsLoading(true);
    setIsError(false);

    optionFetcher(debouncedQuery)
      .then(items => {
        if (cancelled || requestId !== latestRequestId.current) return;
        setResults(items);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled || requestId !== latestRequestId.current) return;
        setResults([]);
        setIsError(true);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // We intentionally trigger on debouncedQuery, not query, so rapid typing
    // collapses to a single fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, optionFetcher]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setIsLoading(false);
      setQuery(null);
    }
  }, [disabled]);

  useEffect(
    () => () => {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
      }
    },
    []
  );

  return (
    <div ref={setContainerEl} className={cn('search-combobox', className)}>
      <SearchField
        ref={inputRef}
        size={size}
        disabled={disabled}
        placeholder={placeholder}
        value={displayValue}
        role='combobox'
        aria-expanded={isOpen && !disabled}
        aria-haspopup='listbox'
        aria-controls={listboxId}
        aria-autocomplete='list'
        aria-activedescendant={
          isOpen && results.length > 0 ? `${listboxId}-option-${highlightedIndex}` : undefined
        }
        onChange={e => {
          const nextValue = e.target.value;
          setQuery(nextValue);
          if (nextValue === '') {
            // Cleared via SearchField's X or by user backspacing to empty —
            // drop any resolved selection so callers see the clear.
            if (value !== null) {
              onChange(null);
            }
            setIsOpen(false);
          } else {
            setIsOpen(true);
          }
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      <Overlay show={isOpen && !disabled} target={containerEl} placement='bottom-start'>
        {({ ref, style }) => (
          <div
            ref={ref}
            role='presentation'
            className='search-combobox-popover'
            style={{ ...style, width: containerEl?.offsetWidth }}
            // Prevent the input from blurring while the user clicks a row.
            onMouseDown={e => e.preventDefault()}
          >
            <Menu
              listboxId={listboxId}
              rows={menuRows}
              loading={isLoading}
              errorMessage={!isLoading && isError ? 'Failed to load results' : undefined}
              emptyMessage='No results'
              highlightedIndex={highlightedIndex}
              onHighlightChange={setHighlightedIndex}
              onRowClick={row => handleSelectByValue(row.value)}
            />
          </div>
        )}
      </Overlay>
    </div>
  );
};
